
import React, { useState } from 'react';
import { ChevronRight, Calendar } from 'lucide-react';
import { TransferNews } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { formatCurrency } from '../../services/footballService';

interface TransferCardProps {
    transfer: TransferNews;
    rank: number;
    onClick: () => void;
    showDate?: boolean;
    hideRank?: boolean;
}

const TransferArrow = ({ className }: { className?: string }) => (
    <svg width="18" height="10" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M2 2L6 6L2 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.1" />
        <path d="M5 2L9 6L5 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.2" />
        <path d="M8 2L12 6L8 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
        <path d="M11 2L15 6L11 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
        <path d="M14 2L18 6L14 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const TransferCard: React.FC<TransferCardProps> = ({ transfer, rank, onClick, showDate = true, hideRank = false }) => {
    const { lang } = useLanguage();
    const [imgError, setImgError] = useState(false);

    const formatTransferDate = (dateStr: string) => {
        if (!dateStr) return '';
        if (lang === 'ko' || lang === 'ja') {
            try {
                // DD/MM/YYYY or YYYY-MM-DD
                let date: Date;
                if (dateStr.includes('/')) {
                    const [d, m, y] = dateStr.split('/').map(Number);
                    date = new Date(y, m - 1, d);
                } else {
                    date = new Date(dateStr);
                }
                
                if (isNaN(date.getTime())) return dateStr;

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}/${month}/${day}`;
            } catch (e) {
                return dateStr;
            }
        }
        return dateStr;
    };

    // player_id가 숫자 형태인 경우 API-Football의 고화질 공식 이미지를 사용
    const playerId = transfer.playerId;
    const isRealId = playerId && !isNaN(Number(playerId));
    const officialImageUrl = isRealId ? `https://media.api-sports.io/football/players/${playerId}.png` : transfer.player.avatar;
    const finalImageUrl = imgError ? `https://ui-avatars.com/api/?background=f1f5f9&color=cbd5e1&bold=true&name=${encodeURIComponent(transfer.player.name)}` : officialImageUrl;
    
    return (
        <div 
            className="group flex items-center gap-3 md:gap-4 bg-white rounded-[18px] md:rounded-[24px] p-3 md:p-4 border border-gray-100 md:hover:border-rose-200 md:hover:bg-gray-50/30 transition-all duration-300 cursor-pointer active:scale-[0.98]"
            onClick={onClick}
        >
            {!hideRank && (
                <div className={`w-8 h-8 flex items-center justify-center rounded-xl text-[10px] font-black shrink-0 ${
                    rank === 0 ? 'bg-rose-500 text-white' :
                    rank === 1 ? 'bg-slate-900 text-white' :
                    'bg-gray-100 text-slate-400'
                }`}>
                    {rank + 1}
                </div>
            )}

            <div className="shrink-0">
                <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-100 bg-gray-50 shadow-sm">
                    <img 
                        src={finalImageUrl} 
                        alt={transfer.player.name} 
                        className="w-full h-full object-cover" 
                        onError={() => setImgError(true)}
                    />
                </div>
            </div>

            <div className="flex-1 min-w-0">
                <h3 className="font-black text-slate-900 text-sm truncate md:group-hover:text-rose-600 transition-colors uppercase tracking-tight">
                    {transfer.player.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                    <img src={transfer.player.prevClubLogo} className="w-5 h-5 object-contain opacity-60" alt="" />
                    <TransferArrow className="text-slate-300 shrink-0" />
                    <img src={transfer.toClub.logo} className="w-5 h-5 object-contain" alt="" />
                </div>
            </div>

            <div className="text-right shrink-0">
                <p className="text-[11px] font-black text-rose-600 tabular-nums leading-none">
                    {formatCurrency(transfer.fee, lang)}
                </p>
                {showDate && (
                    <div className="flex items-center justify-end gap-2 mt-1">
                        <div className="flex items-center gap-1 text-[8px] font-black text-slate-400">
                            <Calendar size={8} />
                            <span className="uppercase tracking-widest">{formatTransferDate(transfer.date)}</span>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="text-gray-200 md:group-hover:text-rose-400 transition-colors">
                <ChevronRight size={16} strokeWidth={3} />
            </div>
            
        </div>
    );
};